import { DatapointStatus, logUsage, PerformanceDatapoint } from 'owa-analytics';
import { apolloErrorToResponseCode } from 'owa-apollo-errors/lib/apolloErrorToResponseCode';
import { getISOString, userDate } from 'owa-datetime';
import { getGuid } from 'owa-guid';
import { isAuthStatusCode } from 'owa-http-status-codes';
import { getBootCustomData } from 'owa-performance';
import { ApolloError } from '@apollo/client';
import fetchPlacesSettings from './fetchPlacesSettings';

import type { MailboxInfo } from 'owa-client-types';
import type { PlacesSettings } from './types/PlacesSettings';
import type { CustomDataMap, TraceErrorObject } from 'owa-analytics-types';
import { tryGetTraceObjectErrorFromApolloError } from 'owa-trace/lib/tryGetTraceObjectErrorFromApolloError';

export const PLACES_SETTINGS_RETRY_BACKOFF_MS: number[] = [2000, 6000];

export default async function retryFetchPlacesSettings(
    mailboxInfo: MailboxInfo,
    shouldUseMainThread?: boolean
): Promise<PlacesSettings> {
    const datapoint = new PerformanceDatapoint('RETRY_FETCH_PLACES_SETTINGS', mailboxInfo);
    const trackId = getGuid();
    const startTime = performance.now();
    let hasError = false;
    let caughtError: Error | undefined;
    const timers: NodeJS.Timeout[] = [];
    let retryCount = 0;
    let successIndex = -1;

    const custom1: Readonly<CustomDataMap> = {
        TrackId: trackId,
        Step: 'Start',
        IsRetryEnabled: true,
        StartTime: getISOString(userDate(startTime + performance.timeOrigin)),
        RetryCount: retryCount,
        SuccessIndex: successIndex,
        ShouldUseMainThread: shouldUseMainThread,
        hostSessionId: getBootCustomData().HostSessionId,
    };

    // Consider the 60s timeout case, so we need to include the following information now
    datapoint.addCustomData(custom1);

    try {
        // Sending the Start log alone without waiting for the End,
        // so that we know it happened even if the WINDOW IS SUDDENLY CLOSED.

        logUsage('RetryFetchPlacesSettingsStart', mailboxInfo, custom1);

        const fetch = (options?: { noBatching?: boolean; queryDeduplication?: boolean }) => {
            return fetchPlacesSettings(
                mailboxInfo,
                shouldUseMainThread,
                undefined /* timeoutMS */,
                options?.noBatching,
                options?.queryDeduplication
            );
        };

        // Retry the request
        return await new Promise<PlacesSettings>((resolve, reject) => {
            Promise.allSettled<void>([
                // Same as in the original code
                fetch()
                    .then(result => {
                        if (successIndex === -1) {
                            successIndex = 0;
                            resolve(result); // Resolve ASAP to avoid waiting for the `Promise.allSettled` to resolve
                        }
                    })
                    .catch(err => {
                        const responseCode =
                            err instanceof ApolloError ? apolloErrorToResponseCode(err) : '';

                        // Stop retrying if the error is an auth error since more retries won't help
                        if (isAuthStatusCode(Number(responseCode))) {
                            reject(err);
                        }

                        throw err;
                    }),

                // Start retry after a backoff
                ...PLACES_SETTINGS_RETRY_BACKOFF_MS.map((backoff, index) => {
                    return new Promise<void>((resolve2, reject2) => {
                        const timer = setTimeout(() => {
                            retryCount += 1;
                            fetch({
                                noBatching: true, // Disable batching to avoid waiting for other requests in the batch
                                queryDeduplication: false, // Disable to allow multiple same requests to be sent concurrently
                            })
                                .then(result => {
                                    if (successIndex === -1) {
                                        successIndex = index + 1;
                                        resolve(result); // Resolve ASAP to avoid waiting for the `Promise.allSettled` to resolve
                                    }
                                    resolve2();
                                })
                                .catch(err => {
                                    reject2(err);
                                });
                        }, backoff);

                        // Clear the timer in the finally block
                        timers.push(timer);
                    });
                }),
            ]).then(([result]) => {
                if (result.status === 'rejected') {
                    reject(result.reason); // All failed, sending out the first error
                }
            });
        });
    } catch (err) {
        hasError = true;
        // Errors resulting from enabling useUnknownInCatchVariables
        // TS2322: Type 'unknown' is not assignable to type 'Error | undefined'.
        // @ts-expect-error
        caughtError = err;
        throw err;
    } finally {
        timers.forEach(clearTimeout);

        const endTime = performance.now();

        const custom2: Readonly<CustomDataMap> = {
            ...custom1,
            Step: hasError ? 'Error' : 'Success',
            EndTime: getISOString(userDate(endTime + performance.timeOrigin)),
            Duration: endTime - startTime,
            RetryCount: retryCount,
            SuccessIndex: successIndex,
            error: typeof caughtError === 'string' ? caughtError : caughtError?.message,
            responseStatus:
                caughtError instanceof ApolloError
                    ? apolloErrorToResponseCode(caughtError)
                    : undefined,
        };

        // Add custom data to the data point
        // Have to overwrite the property bag
        // because `addCustomData` will not overwrite existing properties such as Step, RetryCount, SuccessIndex
        Object.assign(datapoint.getPropertyBag() ?? {}, custom2);

        if (hasError) {
            const traceError = tryGetTraceObjectErrorFromApolloError(
                caughtError
            ) as TraceErrorObject;

            datapoint.addCustomData({
                cv: traceError?.correlationVector ?? 'No CV',
            });

            datapoint.endWithError(DatapointStatus.ServerError, traceError);
        } else {
            datapoint.end();
        }

        // Also log the error to `logUsage` for easier tracking
        /* eslint-disable-next-line owa-custom-rules/forbid-specific-functions-on-catch-finally-blocks -- (https://aka.ms/OWALintWiki)
         * BASELINE. DO NOT COPY AND PASTE!, replace this line with a justification or fix the error, use PerformanceDatapoint instead.
         *	> Do not call 'logUsage' from 'owa-analytics' inside catch or finally blocks */
        logUsage('RetryFetchPlacesSettingsEnd', mailboxInfo, custom2);
    }
}
